// The reach of a side: the fewest moves one of its pieces needs to land on a
// square, while every other piece stays where it is. The AI uses it to aim a
// spell that puts a square on the board: which squares are nearer to which side.

import { REACH_MOVE_LIMIT, SQUARE_COUNT } from '../constants';
import { movesForPiece } from '../engine/moves';
import { squaresOf, withPiece } from '../engine/pieces';
import type { Position, Side } from '../engine/types';

/** The count for a square that no piece of the side reaches within the limit. */
export const OUT_OF_REACH = REACH_MOVE_LIMIT + 1;

const SIDES: readonly Side[] = ['gold', 'violet'];

/** The position with the piece on `from` set on `to`, and nothing else changed. */
const withPieceMovedTo = (position: Position, from: number, to: number): Position => {
  const piece = position.board[from];
  if (!piece || from === to) return position;
  const moved = { ...piece, moved: true };
  return { ...position, board: withPiece(withPiece(position.board, from, null), to, moved) };
};

/** Lowers the count of each square the piece on `from` reaches within the limit. */
const countReachOfPiece = (position: Position, from: number, counts: number[]): void => {
  const seen = new Set<number>([from]);
  let frontier = [from];
  for (let moveCount = 1; moveCount <= REACH_MOVE_LIMIT && frontier.length > 0; moveCount += 1) {
    const next: number[] = [];
    for (const standing of frontier) {
      for (const move of movesForPiece(withPieceMovedTo(position, from, standing), standing)) {
        if (seen.has(move.to)) continue;
        seen.add(move.to);
        next.push(move.to);
        counts[move.to] = Math.min(counts[move.to] ?? OUT_OF_REACH, moveCount);
      }
    }
    frontier = next;
  }
};

/** For each square, the fewest moves a piece of the side needs to land on it. */
export const movesToReach = (position: Position, side: Side): number[] => {
  const counts: number[] = Array.from({ length: SQUARE_COUNT }, () => OUT_OF_REACH);
  for (const from of squaresOf(position.board, side)) countReachOfPiece(position, from, counts);
  return counts;
};

type ReachOfBoard = { rules: Position['rules']; counts: Map<Side, number[]> };

/** The counts of the last rules seen with a board. The AI asks for one board many times in an aim. */
const reachByBoard = new WeakMap<Position['board'], ReachOfBoard>();

const cachedReach = (position: Position, side: Side): number[] => {
  let entry = reachByBoard.get(position.board);
  if (!entry || entry.rules !== position.rules) {
    entry = { rules: position.rules, counts: new Map() };
    reachByBoard.set(position.board, entry);
  }
  const known = entry.counts.get(side);
  if (known) return known;
  const counts = movesToReach(position, side);
  entry.counts.set(side, counts);
  return counts;
};

/**
 * How many moves sooner the side lands on the squares than the other side,
 * as a mean. Above zero, the squares are nearer to the side. No square gives zero.
 */
export const reachLead = (position: Position, squares: readonly number[], side: Side): number => {
  if (squares.length === 0) return 0;
  const other = SIDES.find((each) => each !== side) ?? side;
  const own = cachedReach(position, side);
  const theirs = cachedReach(position, other);
  const total = squares.reduce(
    (sum, square) => sum + (theirs[square] ?? OUT_OF_REACH) - (own[square] ?? OUT_OF_REACH),
    0,
  );
  return total / squares.length;
};
